import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { useOrderForm } from '../contexts/OrderFormContext';
import CompletenessDashboard from '../../../shared/components/CompletenessDashboard';

const ORDER_TYPE_LABELS: Record<string, string> = {
  'e-filing': 'eFiling',
  'court-filing': 'Court Filing',
  'process-serving': 'Process Serving',
  'document-retrieval': 'Document Retrieval',
  'courtesy-copy-delivery': 'Courtesy Copy Delivery',
  'secretary-of-state-filing': 'Secretary of State Filing',
  'county-recording': 'County Recording',
  'skip-trace': 'Skip Trace',
  'eservice': 'eService',
};

interface OrderSummaryPanelProps {
  brand: BrandConfig;
  onSubmit: () => void;
}

const OrderSummaryPanel: React.FC<OrderSummaryPanelProps> = ({ brand, onSubmit }) => {
  const {
    orderType,
    caseNumber,
    caseTitle,
    priority,
    documents,
    isSubmitting,
    submitError,
    completeness,
  } = useOrderForm();

  return (
    <div
      className="border rounded-lg p-4 sticky top-6"
      style={{ borderColor: brand.colors.border }}
    >
      <h3
        className="text-sm font-semibold mb-3"
        style={{ color: brand.colors.text }}
      >
        Order Summary
      </h3>

      {/* Completeness Dashboard */}
      <div className="mb-4">
        <CompletenessDashboard brand={brand} completeness={completeness} />
      </div>

      <div className="space-y-2 mb-4">
        <div>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>Type</p>
          <p className="text-sm font-medium" style={{ color: brand.colors.text }}>
            {ORDER_TYPE_LABELS[orderType] ?? orderType}
          </p>
        </div>

        {caseNumber && (
          <div>
            <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Number</p>
            <p className="text-sm" style={{ color: brand.colors.text }}>{caseNumber}</p>
          </div>
        )}

        {caseTitle && (
          <div>
            <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Title</p>
            <p className="text-sm" style={{ color: brand.colors.text }}>{caseTitle}</p>
          </div>
        )}

        <div>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>Priority</p>
          <p className="text-sm capitalize" style={{ color: brand.colors.text }}>
            {priority}
          </p>
        </div>

        <div>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>Documents</p>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            {documents.length} uploaded
          </p>
        </div>
      </div>

      {submitError && (
        <div
          className="mb-3 p-2 rounded text-xs"
          style={{
            backgroundColor: '#FEF2F2',
            color: '#DC2626',
            border: '1px solid #FECACA',
          }}
        >
          {submitError}
        </div>
      )}

      <button
        onClick={onSubmit}
        disabled={isSubmitting}
        className="w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        style={{
          backgroundColor: brand.colors.primary,
          opacity: isSubmitting ? 0.6 : 1,
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          color: brand.colors.onPrimary,
        }}
      >
        {isSubmitting ? 'Submitting...' : 'Submit Order'}
      </button>
    </div>
  );
};

export default OrderSummaryPanel;
